// Where the visit came from, kept across the moment the app has no session yet.
//
// The tags are read off the URL as soon as the app loads, but they can only be
// reported once there is a token to attach them to — and anonymous auth runs on
// a few routes only (ANON_ROUTES in init.js). A link from an email lands
// wherever the letter pointed, so reading and reporting are two steps with
// localStorage in between: capture now, report at the first sign of a session.

import {getStored, parseStored} from '../persistence';

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/** Which *send* the visitor followed, as opposed to which letter: the same
 *  letter goes to thousands of people, and only this says which of them is
 *  here. Kept out of UTM_KEYS because it is not an attribution tag — it is
 *  stripped from the URL alongside them, but it means something else and the
 *  backend resolves it to an account rather than to a source. */
export const DELIVERY_KEY = 'e';

const STORAGE_KEY = 'utm';

/** How long a visit stays credited to the letter that brought it — the usual
 *  attribution window. Without it a single click on an email would keep stamping
 *  its id on every event for the life of the browser profile, crediting the
 *  mailing for purchases that came from somewhere else months later. */
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export type Utm = Partial<Record<typeof UTM_KEYS[number] | typeof DELIVERY_KEY, string | null>>;

type StoredUtm = Utm & { reported?: boolean; at?: number };

/** The answers of `letterFromEmail` / `deliveryFromEmail`, read once per page.
 *  `null` = not read yet. */
let letter: string | undefined | null = null;
let delivery: string | undefined | null = null;

const read = (): StoredUtm | null => parseStored<StoredUtm>(getStored(STORAGE_KEY));

const write = (value: StoredUtm): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        // Storage is unavailable — the visit goes unattributed rather than failing.
    }
};

/** Snapshot the tags this page was opened with. Call before anything rewrites the query. */
export const captureUtm = (search: string): void => {
    const params = new URLSearchParams(search);
    if (!params.has('utm_source')) return;
    letter = delivery = null;   // a new arrival replaces whatever the last one was credited to
    const delivered = params.get(DELIVERY_KEY);
    write({
        ...Object.fromEntries(UTM_KEYS.map(key => [key, params.get(key)])),
        // Only when there is one: most arrivals carry no send id, and a null on
        // every stored visit would ride along into every event's payload.
        ...(delivered ? {[DELIVERY_KEY]: delivered} : {}),
        reported: false,
        at: Date.now(),
    });
};

/** The captured tags still waiting to be sent, or null when there is nothing to send. */
export const pendingUtm = (): Utm | null => {
    const stored = read();
    // A value written before this file existed was stored *after* it was sent.
    if (!stored || stored.reported !== false) return null;
    const {reported, at, ...utm} = stored;
    return utm;
};

export const markUtmReported = (): void => {
    const stored = read();
    if (stored) write({...stored, reported: true});
};

/** Whatever was captured, while the visit is still inside the attribution window. */
const captured = (stored: StoredUtm | null, key: keyof Utm): string | undefined => {
    const fresh = stored?.at !== undefined && Date.now() - stored.at < ATTRIBUTION_WINDOW_MS;
    return fresh ? stored?.[key] || undefined : undefined;
};

/**
 * The letter this visitor arrived from, while it is still what brought them
 * here. Every GTM event carries it, so a purchase can be read back to the email
 * that started it — which the tags alone cannot answer, because they are
 * stripped from the URL a moment after the app opens.
 *
 * Read once: capture happens at module load, before any event can be sent, and
 * nothing changes it for the rest of the page's life.
 */
export const letterFromEmail = (): string | undefined => {
    if (letter !== null) return letter;
    // Guarded by the source: utm_content is set by every campaign we run, so
    // only an email arrival makes it a letter id.
    const stored = read();
    letter = stored?.utm_source === 'email' ? captured(stored, 'utm_content') : undefined;
    return letter;
};

/**
 * The individual send this visitor followed, while it is still what brought
 * them here. `letterFromEmail` says which letter; this says which copy of it,
 * and so — once the backend resolves it — who is reading. It is the only thing
 * that names a visitor who has not signed in.
 */
export const deliveryFromEmail = (): string | undefined => {
    if (delivery !== null) return delivery;
    // Not guarded by the source, deliberately: nothing but a letter ever sets
    // this key, and a visit that reached the app through a landing CTA has had
    // its utm_source overwritten with `landing` on the way.
    delivery = captured(read(), DELIVERY_KEY);
    return delivery;
};

